/// <reference types="vite/client" />
import type { ReactiveController, ReactiveControllerHost } from 'lit';
import Konva from 'konva';
import type { BoardState } from '../state/board-state.js';
import type { SelectionController } from '../selection/selection.controller.js';
import type { ToolController } from '../tool/tool.controller.js';
import type { Tool, ToolChangedDetail } from '../tool/tool.types.js';
import { TOOL_CHANGED_EVENT } from '../tool/tool.types.js';
import { FSM } from '../fsm/fsm.js';
import {
  DRAG_TRANSITIONS,
  type DragEvent,
  type DragState as DragFsmState,
} from '../fsm/drag.fsm.js';

export interface WorldBox { x: number; y: number; width: number; height: number; }

/**
 * Callbacks the InteractionController invokes on the board element.
 *
 * Drag-to-create flow (used by tool modes such as `sticky`):
 *   1. mousedown on empty canvas   → `showDragPreview({...})` (0-sized box)
 *   2. mousemove while down        → `showDragPreview({...})` (updated box)
 *   3. mouseup                     → `showDragPreview(null)` then
 *                                    `placeStickyAt(x, y, sizeOrNull)`
 *      - If the user barely moved, `size` is `null` and the element
 *        falls back to a default-sized object centred at the pointer
 *        (matches the old click-to-place behaviour).
 *      - Otherwise `size` is the dragged width/height.
 */
export interface InteractionCallbacks {
  /**
   * True while a pan is armed or running (Space held, or a pan drag under way).
   *
   * Asked BEFORE any tool reacts to a press, because "hold Space and drag to move the work
   * surface" is a fixed navigation decision and therefore outranks the selected tool. Passed in
   * rather than read from the viewport controller directly so this controller keeps knowing
   * nothing about it — the same shape as `snapWorldPoint` for the grid.
   */
  isPanArmed(): boolean;

  placeStickyAt(worldX: number, worldY: number, size: { width: number; height: number } | null): void;
  placeTextAt(worldX: number, worldY: number, size: { width: number; height: number } | null): void;
  /**
   * Called when the user finishes drawing a line (mouseup after drag).
   * Both endpoints are in world coordinates.
   */
  placeLineAt(x1: number, y1: number, x2: number, y2: number): void;
  placeCheckboxAt(worldX: number, worldY: number): void;
  /**
   * Faengt einen Weltpunkt aufs Raster (Anke, 2026-07-16). Gibt den Punkt
   * unveraendert zurueck, wenn das Raster aus ist — der Controller muss den
   * Zustand darum nicht kennen.
   */
  snapWorldPoint(point: { x: number; y: number }): { x: number; y: number };
  placePersonAt(worldX: number, worldY: number, size: { width: number; height: number } | null): void;
  placeRectangleAt(worldX: number, worldY: number, size: { width: number; height: number } | null): void;
  placeEllipseAt(worldX: number, worldY: number, size: { width: number; height: number } | null): void;
  placeSmileyAt(worldX: number, worldY: number, size: { width: number; height: number } | null): void;
  /** Show a live preview while dragging (null = hide). */
  showDragPreview(box: WorldBox | null): void;
  /** Show a live line preview while drawing (null = hide). */
  showLinePreview(x1: number, y1: number, x2: number, y2: number): void;
  hideLinePreview(): void;
  /**
   * Show / hide the marquee selection rectangle in world coordinates.
   * Passing `null` removes the visual.
   */
  showMarquee(box: WorldBox | null): void;
  /**
   * Called on mouseup after a marquee drag: select all objects whose
   * world bounding box intersects `box`.
   */
  selectInRect(box: WorldBox): void;
  selectOnly(id: string): void;
  addToSelection(id: string): void;
  /**
   * Anke, 2026-07-22 — Shift/Strg-Klick auf ein Objekt schaltet dessen Auswahl
   * UM: ist es bereits ausgewählt, wird es abgewählt, sonst hinzugefügt. Die
   * Gruppen-Auflösung (Klick auf ein Mitglied betrifft die ganze Gruppe) steckt
   * in der Implementierung, darum ein eigener Rückruf statt add + eine
   * `has`-Abfrage hier im Controller.
   */
  toggleSelection(id: string): void;
  clearSelection(): void;
  /**
   * Anke, 2026-09-11 — a plain click (no modifier key, not a drag: Konva's
   * `click` event already excludes drag gestures) landed on an object that
   * was ALREADY selected before this click. Currently only meaningful for
   * textboxes ("ein zweites Mal auf die bereits ausgewählte Box klicken...
   * dann soll man direkt mit dem Cursor im Text landen") — the element
   * decides per object type what (if anything) this means; every other
   * type is a no-op, same as before this callback existed.
   *
   * Anke, 2026-09-12 — `clientX`/`clientY` are the raw native-MouseEvent
   * viewport coordinates of the click (straight off `e.evt`, not Konva
   * world/content-layer coordinates), passed through so the caret can land
   * where the pointer actually was instead of jumping to the text start.
   */
  onClickAlreadySelected(id: string, clientX: number, clientY: number): void;
}

/** Pixels of cursor travel (in world space) below which we treat the gesture as a click. */
const DRAG_THRESHOLD = 4;

/**
 * Drag context tracked alongside the FSM. Lifecycle is synchronised with
 * `dragFsm` transitions (see PRD-D Sub-Aufgabe 5):
 * set on a successful pointer-down transition, mutated on pointer-move,
 * cleared after pointer-up / escape via `resetDragContext()`.
 */
interface DragContext {
  tool: Tool;
  startWorld: { x: number; y: number };
  lastWorld: { x: number; y: number };
  moved: boolean;
  /** Whether Shift was held during the latest pointer move (1:1 constraint). */
  shiftKey: boolean;
}

/**
 * DEV-only transition logger for the drag FSM. In test/prod builds
 * (`import.meta.env.DEV === false`) this is `undefined`, so no console spam.
 */
const dragTransitionLogger: ((from: DragFsmState, event: DragEvent, to: DragFsmState) => void) | undefined =
  import.meta.env.DEV
    ? (from, event, to) => console.debug('[drag-fsm]', from, '→', to, `(${event})`)
    : undefined;

/**
 * Routes Konva stage events to the right action for the current tool.
 *
 * M4 handles:
 *   - tool=select : click object → select, click empty → clear selection
 *   - tool=sticky : drag on empty canvas → place a sized sticky note,
 *                   bare click → place a default-sized sticky note,
 *                   then return to select in either case
 */
export class InteractionController implements ReactiveController {
  private readonly host: ReactiveControllerHost & HTMLElement;
  private readonly stage: Konva.Stage;
  private readonly state: BoardState;
  private readonly selection: SelectionController;
  private readonly tools: ToolController;
  private readonly cb: InteractionCallbacks;

  /**
   * Drag-FSM (T6.2d). Replaces the legacy `drag?: DragState` flag-tracker.
   * Pointer-down → enters a drag state; pointer-up / escape → returns to idle.
   */
  private readonly dragFsm = new FSM<DragFsmState, DragEvent>(
    'idle',
    DRAG_TRANSITIONS,
    dragTransitionLogger,
  );
  /** Drag context paired with `dragFsm`. Populated on successful pointer-down
   *  transition, cleared via `resetDragContext()` on pointer-up / escape. */
  private dragStart?: DragContext;
  /** Set to true when a placement happens on mousedown so the immediately
   *  following stage click event doesn't clear the new selection. */
  private suppressNextClear = false;
  /** Readonly-Mode flag (TR9). When true, onStageDown is a complete no-op. */
  private isReadonly = false;

  constructor(
    host: ReactiveControllerHost & HTMLElement,
    stage: Konva.Stage,
    state: BoardState,
    selection: SelectionController,
    tools: ToolController,
    cb: InteractionCallbacks,
  ) {
    this.host = host;
    this.stage = stage;
    this.state = state;
    this.selection = selection;
    this.tools = tools;
    this.cb = cb;
    host.addController(this);
  }

  hostConnected(): void {
    this.stage.on('click.interaction tap.interaction', this.onStageClick);
    this.stage.on('mousedown.interaction touchstart.interaction', this.onStageDown);
    this.host.addEventListener(TOOL_CHANGED_EVENT, this.onToolChanged as EventListener);
  }

  hostDisconnected(): void {
    this.stage.off('click.interaction tap.interaction');
    this.stage.off('mousedown.interaction touchstart.interaction');
    this.host.removeEventListener(TOOL_CHANGED_EVENT, this.onToolChanged as EventListener);
    this.cancelDrag();
  }

  /**
   * Cross-FSM effect: when the active tool changes mid-drag, force the drag FSM
   * back to `idle` and clear all drag-related visuals + context. No-op when the
   * FSM is already idle (e.g. tool change without an active drag).
   */
  private onToolChanged = (_e: CustomEvent<ToolChangedDetail>): void => {
    if (this.dragFsm.current !== 'idle') {
      this.dragFsm.send('escape');
      this.cleanupDragVisuals();
      this.resetDragContext();
    }
  };

  // ---- Click: selection only (tool=select) -----------------------------

  private onStageClick = (e: Konva.KonvaEventObject<MouseEvent>): void => {
    // Always consume the suppression flag, regardless of click target.
    // If the click lands on the newly-placed object (same position as placement),
    // e.target is the object — not the stage — so we must still clear the flag.
    const wasSuppressed = this.suppressNextClear;
    this.suppressNextClear = false;

    // Bugfix, Anke 2026-09-16: readonly forces the tool to `select`
    // (`lockToSelect()`), and this handler only ever checked the TOOL, not
    // `readonly` — so a locked board still let objects be clicked/selected
    // (selection handles + options circle appeared) even though nothing
    // about them could actually be changed. `onStageDown` already has this
    // same `isReadonly` guard for drag/marquee gestures; `onStageClick` is a
    // SEPARATE Konva event (`click.interaction`, not `mousedown.interaction`)
    // that never had it.
    if (this.isReadonly) return;

    if (this.tools.tool !== 'select') return;

    if (e.target === this.stage) {
      if (wasSuppressed) return;
      this.cb.clearSelection();
      return;
    }
    const id = this.findObjectIdFromEvent(e.target);
    // If the click doesn't land on a known board object — including clicks
    // on the Transformer's invisible bounding-rect fill — treat it as
    // a click on the empty canvas and clear the selection.
    if (!id) { this.cb.clearSelection(); return; }

    if (e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey) {
      // Umschalten statt nur hinzufügen: ein erneuter Shift-Klick auf ein bereits
      // ausgewähltes Objekt wählt es wieder ab (Anke, 2026-07-22).
      this.cb.toggleSelection(id);
    } else if (!this.selection.has(id)) {
      this.cb.selectOnly(id);
    } else {
      // Anke, 2026-09-11 — a plain click on an object that was ALREADY
      // selected before this click (no modifier, and Konva's `click` event
      // itself already excludes a drag gesture). Previously a no-op; now
      // routed to the element, which decides per object type (currently:
      // textboxes only — "ein zweites Mal... dann soll man direkt mit dem
      // Cursor im Text landen").
      this.cb.onClickAlreadySelected(id, e.evt.clientX, e.evt.clientY);
    }
  };

  // ---- Mousedown: maybe start a drag-to-create or marquee gesture ------

  /**
   * Toggle readonly mode (TR9). While true, all stage-down gestures are
   * blocked. Toggling to true while a drag is in progress cancels the drag
   * cleanly (covers AC12 — runtime toggle).
   */
  setReadonly(value: boolean): void {
    if (this.isReadonly === value) return;
    this.isReadonly = value;
    if (value) this.cancelDrag();
  }

  private onStageDown = (e: Konva.KonvaEventObject<MouseEvent>): void => {
    if (this.isReadonly) return;
    // Dismiss any pending suppress-clear guard at the start of every new
    // pointer gesture. This handles the case where the checkbox creation
    // click lands on the checkbox itself (cancelBubble = true prevents
    // onStageClick from running), so the flag would otherwise never be
    // cleared and the NEXT click on empty canvas would be wrongly suppressed.
    this.suppressNextClear = false;

    // Panning outranks every tool, and this is where that is decided.
    //
    // It has to be a check here rather than a fix to listener order. The placement branch below
    // calls stopPropagation() on the native event, and the pan's own mousedown listener sits on
    // the Konva container in the BUBBLE phase — so the press never reaches it and the pan dies
    // silently. Moving the pan to the capture phase would not help either: then BOTH would run,
    // and a Space-drag would pan and draw an object at once. The gesture has one meaning.
    //
    // `suppressNextClear` is set above this point on purpose — a pan is a new gesture and should
    // clear that guard like any other.
    if (this.cb.isPanArmed()) return;

    const tool = this.tools.tool;

    const pointer = this.stage.getPointerPosition();
    if (!pointer) return;
    const world = this.stageToWorld(pointer);

    if (tool === 'select') {
      // Only begin a marquee when the press lands on empty canvas —
      // object clicks must still reach the object for drag/selection.
      if (e.target !== this.stage) return;
      this.dragFsm.send('pointer-down-on-empty');
      this.dragStart = { tool: 'select', startWorld: world, lastWorld: world, moved: false, shiftKey: e.evt.shiftKey };
      window.addEventListener('mousemove', this.onWindowMove);
      window.addEventListener('mouseup', this.onWindowUp);
      e.evt.preventDefault();
      return;
    }

    // Checkbox: place immediately on mousedown — no drag.
    if (tool === 'checkbox') {
      this.suppressNextClear = true;
      const p = this.cb.snapWorldPoint(world);
      this.cb.placeCheckboxAt(p.x, p.y);
      this.tools.resetToSelect();
      // Stop the event so the underlying object (if any) doesn't start dragging.
      e.cancelBubble = true;
      e.evt.preventDefault();
      e.evt.stopPropagation();
      return;
    }

    // Placement tools → drag-to-create wins, regardless of whether the
    // pointer landed on empty canvas or on an existing object. Stop the
    // event so the object doesn't react to the press (no drag, no select).
    if (tool !== 'sticky' && tool !== 'text' && tool !== 'line' && tool !== 'person' && tool !== 'rectangle' && tool !== 'ellipse' && tool !== 'smiley') return;

    // Anke, 2026-07-16 — der Startpunkt selbst rastet ein: Wer ein Objekt
    // aufzieht, soll am naechsten Rasterpunkt BEGINNEN, nicht am rohen Klick.
    // Deshalb faengt der Start schon hier, beim Aufsetzen. Die Vorschau waechst
    // dann von diesem Punkt, und das fertige Objekt hat dort seine Startecke;
    // die Groesse bleibt frei (nur der Start ist gefragt). `snapWorldPoint` ist
    // die Identitaet, wenn das Raster aus ist.
    const startWorld = this.cb.snapWorldPoint(world);
    this.dragFsm.send('pointer-down-with-tool');
    this.dragStart = { tool, startWorld, lastWorld: startWorld, moved: false, shiftKey: e.evt.shiftKey };
    this.cb.showDragPreview({ x: startWorld.x, y: startWorld.y, width: 0, height: 0 });

    window.addEventListener('mousemove', this.onWindowMove);
    window.addEventListener('mouseup', this.onWindowUp);

    e.cancelBubble = true;
    e.evt.preventDefault();
    e.evt.stopPropagation();
  };

  private onWindowMove = (e: MouseEvent): void => {
    if (!this.dragStart) return;
    const stagePos = this.pointerFromClient(e.clientX, e.clientY);
    const world = this.stageToWorld(stagePos);
    this.dragStart.lastWorld = world;
    this.dragStart.shiftKey = e.shiftKey;

    const rawBox = boxFromCorners(this.dragStart.startWorld, world);
    // Review-Lauf 01, Linse A (SUGGESTION): unified to `>=` — this used to be
    // `>` while `isDrag` below compares with `>=`, so a movement of EXACTLY
    // DRAG_THRESHOLD px left `moved` false while `isDrag`'s own box check
    // would have accepted it, and the `line` tool (gated on `moved`, not
    // `isDrag`) would have silently swallowed that edge-case drag.
    if (rawBox.width >= DRAG_THRESHOLD || rawBox.height >= DRAG_THRESHOLD) this.dragStart.moved = true;

    const box = resolveDragBox(this.dragStart.tool, this.dragStart.shiftKey, this.dragStart.startWorld, world);

    if (this.dragStart.tool === 'line') {
      this.cb.showLinePreview(
        this.dragStart.startWorld.x, this.dragStart.startWorld.y,
        world.x, world.y,
      );
    } else if (this.dragStart.tool === 'select') {
      if (this.dragStart.moved) this.cb.showMarquee(rawBox);
    } else {
      this.cb.showDragPreview(box);
    }
  };

  private onWindowUp = (): void => {
    if (!this.dragStart) return;
    const ds = this.dragStart;
    this.dragFsm.send('pointer-up');
    this.resetDragContext();
    this.cleanupDragVisuals();

    const box = resolveDragBox(ds.tool, ds.shiftKey, ds.startWorld, ds.lastWorld);
    const isDrag = ds.moved && box.width >= DRAG_THRESHOLD && box.height >= DRAG_THRESHOLD;

    const size = isDrag ? { width: box.width, height: box.height } : null;
    // Der Startpunkt ist bereits beim Aufsetzen gefangen (onStageDown). Beim
    // Ziehen nach rechts/unten ist er zugleich die linke obere Ecke; nach
    // links/oben ist die Startecke die gegenueberliegende — in beiden Faellen
    // beginnt das Objekt am Rasterpunkt. Hier wird darum NICHT erneut gefangen:
    // Ein zweites Fangen der Ecke wuerde die Startecke beim Loslassen wieder vom
    // Raster wegschieben. Die Groesse bleibt frei.
    const anchorX = isDrag ? box.x : ds.startWorld.x;
    const anchorY = isDrag ? box.y : ds.startWorld.y;

    if (ds.tool === 'select') {
      if (isDrag) {
        this.cb.selectInRect(box);
      }
    } else if (ds.tool === 'sticky') {
      // Stefan, 2026-08-31 — a bare click (no drag) must NOT place an
      // element anymore; only an actual drag-out area does. `size` is only
      // non-null when `isDrag` is true (see above), so gating on `isDrag`
      // here also means every `placeXAt` call below is always handed a real
      // size, never `null` — the `size: null` (default-size-on-click) branch
      // inside each `placeXAt`/placement-controller becomes unreachable from
      // the UI, but is deliberately left in place (other, non-UI callers /
      // tests may still construct a default-sized object explicitly). No
      // `resetToSelect()` on a no-op click either — mirrors `cancelDrag()`
      // (Escape): an aborted/empty gesture leaves the tool armed so the user
      // can just try dragging again, instead of forcing a re-select of the
      // tool after every accidental click.
      if (isDrag) {
        this.cb.placeStickyAt(anchorX, anchorY, size);
        this.tools.resetToSelect();
      }
    } else if (ds.tool === 'text') {
      if (isDrag) {
        this.cb.placeTextAt(anchorX, anchorY, size);
        this.tools.resetToSelect();
      }
    } else if (ds.tool === 'line') {
      // Endpunkte aufs Raster faengen. Das Andocken an Objektkanten hat
      // Vorrang: Es wird als pendingStart/EndAnchor separat weitergereicht und
      // ueberschreibt diese Koordinaten im LineLifecycleController.
      //
      // Gated on `ds.moved`, NOT `isDrag`: `isDrag` requires movement in
      // BOTH box dimensions (an area), which a perfectly horizontal or
      // vertical line legitimately never has (zero height or width) — using
      // it here would silently swallow the most common straight-line drags.
      // `moved` only requires movement past DRAG_THRESHOLD in EITHER
      // dimension (see `onWindowMove`), which is the correct "did the user
      // actually drag" measure for a two-point tool.
      if (ds.moved) {
        const a = this.cb.snapWorldPoint(ds.startWorld);
        const b = this.cb.snapWorldPoint(ds.lastWorld);
        this.cb.placeLineAt(a.x, a.y, b.x, b.y);
        this.tools.resetToSelect();
      }
    } else if (ds.tool === 'person') {
      if (isDrag) {
        this.cb.placePersonAt(anchorX, anchorY, size);
        this.tools.resetToSelect();
      }
    } else if (ds.tool === 'rectangle') {
      if (isDrag) {
        this.cb.placeRectangleAt(anchorX, anchorY, size);
        this.tools.resetToSelect();
      }
    } else if (ds.tool === 'ellipse') {
      if (isDrag) {
        this.cb.placeEllipseAt(anchorX, anchorY, size);
        this.tools.resetToSelect();
      }
    } else if (ds.tool === 'smiley') {
      if (isDrag) {
        this.cb.placeSmileyAt(anchorX, anchorY, size);
        this.tools.resetToSelect();
      }
    }
  };

  private cancelDrag(): void {
    if (!this.dragStart && this.dragFsm.current === 'idle') return;
    this.dragFsm.send('escape');
    this.resetDragContext();
    this.cleanupDragVisuals();
  }

  /**
   * Clears the drag context and removes the window-level pointer listeners.
   * Idempotent: safe to call when no drag is in progress.
   */
  private resetDragContext(): void {
    this.dragStart = undefined;
    window.removeEventListener('mousemove', this.onWindowMove);
    window.removeEventListener('mouseup', this.onWindowUp);
  }

  /**
   * Clears all drag-related visual overlays. Each callback is idempotent and
   * safe to call when no overlay is active. Shared by `onWindowUp`,
   * `cancelDrag` and the upcoming T7.2 cross-FSM tool-changed effect.
   */
  private cleanupDragVisuals(): void {
    this.cb.showDragPreview(null);
    this.cb.showMarquee(null);
    this.cb.hideLinePreview();
  }

  // ---- helpers ---------------------------------------------------------

  /** Walk up the node chain to find the owning BoardObject id. */
  private findObjectIdFromEvent(target: Konva.Node): string | null {
    let node: Konva.Node | null = target;
    while (node && node !== this.stage) {
      const id = node.id();
      if (id && this.state.getObject(id)) return id;
      node = node.getParent();
    }
    return null;
  }

  private stageToWorld(screen: { x: number; y: number }): { x: number; y: number } {
    const vp = this.state.viewport;
    return {
      x: (screen.x - vp.x) / vp.scale,
      y: (screen.y - vp.y) / vp.scale,
    };
  }

  private pointerFromClient(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.stage.container().getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }
}

/**
 * Resolve the drag-to-create box for a tool, honouring the 1:1 constraint.
 *
 * Smiley is always square; an ellipse becomes a circle while Shift is held
 * (InDesign-style). Every other tool produces a free rectangle from the two
 * drag corners. Pure function so the constraint logic is unit-testable in
 * isolation from the Konva event plumbing.
 */
export function resolveDragBox(
  tool: Tool,
  shiftKey: boolean,
  start: { x: number; y: number },
  cur: { x: number; y: number },
): WorldBox {
  const constrainToSquare = tool === 'smiley' || (tool === 'ellipse' && shiftKey);
  return constrainToSquare ? squareBoxFromStart(start, cur) : boxFromCorners(start, cur);
}

function boxFromCorners(a: { x: number; y: number }, b: { x: number; y: number }): WorldBox {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(b.x - a.x),
    height: Math.abs(b.y - a.y),
  };
}

/**
 * Like `boxFromCorners`, but constrains the result to a 1:1 square anchored
 * on the start point. The square's side is the smaller of the two raw deltas
 * so the shape never extends past the cursor in either axis, and follows
 * the drag direction (up/left/down/right).
 */
function squareBoxFromStart(start: { x: number; y: number }, cur: { x: number; y: number }): WorldBox {
  const dx = cur.x - start.x;
  const dy = cur.y - start.y;
  const side = Math.min(Math.abs(dx), Math.abs(dy));
  const signX = dx < 0 ? -1 : 1;
  const signY = dy < 0 ? -1 : 1;
  const endX = start.x + signX * side;
  const endY = start.y + signY * side;
  return {
    x: Math.min(start.x, endX),
    y: Math.min(start.y, endY),
    width: side,
    height: side,
  };
}
